import { useQuery } from "@blitzjs/rpc"
import { TrashIcon, ExclamationTriangleIcon } from "@radix-ui/react-icons"
import { FormProps } from "app/core/components/Form"
import { Scenario, Area, AreaOnScenario, Blockgroup, BlockgroupOnScenario } from "db"
import { Field, FieldProps, Form, Formik, useFormikContext } from "formik"
import getBlockgroupOnScenario from "app/blockgroup-on-scenarios/queries/getBlockgroupOnScenario"
import React, { useContext, useState } from "react"
import { z } from "zod"
import { getAreasOnBlockgroup } from "app/utils/scenario_utils"
import {
  getAreaSum,
  getAcresNeeded,
  approxTreeCount,
  calculateTreeEquityScoreIncrease,
  getScenarioTotalProposedTreeCount,
  calculateScenarioTotalTreeCanopySqft,
  totalTrees,
} from "app/utils/scenario_math"
import { UpdateAreaOnScenario } from "app/validations"
import { styledButton2 } from "components/elements"
import Help from "components/help"
export { FORM_ERROR } from "app/core/components/Form"
import { TYPES, TREE_INFO, ZERO_TREES, DEFAULT_TARGET_INCREASE } from "app/constants"
import { formatNumber } from "app/utils/formatting_utils"
import {
  squareFeetToAcres,
  squareFeetToSquareMeters,
  squareKilometersToSquareFeet,
} from "app/utils/conversion_utils"
import { ScenarioBuilderSlider } from "components/scenario_builder_slider"
import { CityContext } from "app/features/regional-map/regional-map.state"
import { validateZodSchema } from "blitz"

type TypeKey = (typeof TYPES)[number]
type AreaOnScenarioValues = z.infer<typeof UpdateAreaOnScenario>

// Presentational blockgroup panel. Reads the single parent form via useFormikContext — the
// slider writes `treeEquityScoreTarget` onto that form, and the save always writes that value
// onto the blockgroup (see the mutation). The value starts as the blockgroup's current target,
// so leaving the slider alone re-writes the same value rather than clobbering it. The slider is
// locked behind an "Adjust Target" click (isSliderDisabled) purely as UX — planting is the primary
// action here — and that gate has no bearing on what's persisted: the current value is sent either way.
function BlockGroupOverview({
  area,
  areaOnScenario,
  blockgroup,
  blockgroupScenario,
  blockgroupOnScenario,
  isSubmitting,
  onCancel,
}: {
  area: Area
  areaOnScenario: AreaOnScenario | null
  blockgroup: Blockgroup
  blockgroupScenario: Scenario
  blockgroupOnScenario: BlockgroupOnScenario | null
  isSubmitting: boolean
  onCancel: () => void
}) {
  const city = useContext(CityContext)!
  const { values, touched } = useFormikContext<AreaOnScenarioValues>()
  // UX-only gate: the target slider starts locked (planting is the primary action) and the user
  // opts into adjusting it. It does not change what's saved — the form always sends the target.
  const [isSliderDisabled, setIsSliderDisabled] = useState(true)
  const toggleSliderState = () => setIsSliderDisabled(!isSliderDisabled)

  const areas = getAreasOnBlockgroup(blockgroupScenario, blockgroup)

  // canopy proposed in the currently selected parcel/right-of-way
  let plannedCanopy = usePlannedArea()
  let proposedTrees = totalTrees(values)

  if (areas.length) {
    const selectedAreaId = values.areaId
    // usePlannedArea() sums the canopy proposed in the currently selected area, filter the
    // selected area out so its proposed canopy isn't double counted
    const otherAreas = areas.filter((area: AreaOnScenario) => area.areaId != selectedAreaId)
    plannedCanopy += calculateScenarioTotalTreeCanopySqft(otherAreas)
    proposedTrees += getScenarioTotalProposedTreeCount(otherAreas)
  }
  const improvedTarget = calculateTreeEquityScoreIncrease(
    blockgroup,
    squareFeetToAcres(plannedCanopy)
  )

  return (
    <>
      <div className="border-b-2">
        <p className="p-4 capitalize text-brand-green-dark">{`${
          city.locale && city.locale === "en-CA" ? "Census tract" : "Block group"
        } overview | ${area.blockgroupId}`}</p>
        {!touched.treeEquityScoreTarget && !blockgroupOnScenario && (
          <div className="pl-2 text-annotation text-gray-600">
            A default target of +5 has been set for this{" "}
            {city.locale && city.locale === "en-CA" ? "census tract" : "block group"}'s Tree Equity
            Score. Adjust it below if needed.
          </div>
        )}
        <div className="flex flex-row px-4 pt-10">
          <div className="mr-2">
            <p className="-mb-1 text-center text-[0.65rem] font-bold uppercase text-gray-500">
              starting
            </p>
            <p className="text-center text-caption font-bold uppercase text-gray-500">
              {blockgroup.tree_equity_score}
            </p>
          </div>
          <Field
            min={blockgroup.tree_equity_score}
            max={100}
            component={ScenarioBuilderSlider}
            disabled={isSliderDisabled}
            className="w-3/4"
            name="treeEquityScoreTarget"
            defaultValue={values.treeEquityScoreTarget}
            improvedTarget={improvedTarget}
          />
          <div className="ml-2">
            <p className="-mb-1 text-center text-annotation font-bold uppercase text-gray-500">
              max
            </p>
            <p className="text-center text-caption font-bold uppercase text-gray-500">100</p>
          </div>
        </div>
        <div className="mt-6 flex flex-row items-center p-4 text-annotation">
          <div className="border-r-2 px-2">
            <p className="font-bold text-brand-green">
              {approxTreeCount(getAcresNeeded(blockgroup, values.treeEquityScoreTarget ?? 0))}
            </p>
            <p className="text-gray-500">approx. trees needed to reach the target</p>
          </div>
          <div className="border-r-2 px-2">
            <p className="font-bold text-brand-green">{`${proposedTrees}`}</p>
            <p className="text-gray-500">
              trees planted in this{" "}
              {city.locale && city.locale === "en-CA" ? "census tract" : "block group"}
            </p>
          </div>
          {isSliderDisabled ? (
            <div className="pl-1">
              <button
                className={styledButton2({ variant: "outline" })}
                type="button"
                onClick={toggleSliderState}
              >
                Adjust Target
              </button>
            </div>
          ) : (
            <p className="pl-2 text-gray-500">Update scenario below to save new target</p>
          )}
        </div>
      </div>
      <div className="flex justify-center gap-x-2 p-4">
        <button
          type="submit"
          className={styledButton2({ variant: "primary" })}
          disabled={isSubmitting}
        >
          {areaOnScenario
            ? isSliderDisabled
              ? "Update Planting"
              : "Update Planting & Target"
            : "Add To Scenario"}
        </button>
        <button className={styledButton2({ variant: "outline" })} type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </>
  )
}

export function AreaOnScenarioForm({
  area,
  scenario,
  areaOnScenario,
  onSubmit,
  onCancel,
}: FormProps<typeof UpdateAreaOnScenario> & {
  area: Area
  scenario: Scenario
  areaOnScenario: AreaOnScenario | null
  onCancel: () => void
}) {
  const schema = UpdateAreaOnScenario
  const [formError, setFormError] = useState<string | null>(null)
  const potential_tree_canopy_sqft = squareKilometersToSquareFeet(
    area.area_sqkm * area.potential_tree_canopy
  )
  const city = useContext(CityContext)!

  // The blockgroup (+ its existing scenario row, if any) drives the target slider's initial
  // value and the "trees needed" math. Loaded here so the single form owns all its state.
  const [{ blockgroup, scenario: blockgroupScenario, blockgroupOnScenario }] = useQuery(
    getBlockgroupOnScenario,
    {
      scenarioId: scenario.id,
      blockgroupId: area.blockgroupId,
    }
  )

  const areaInitialValues = areaOnScenario || {
    scenarioId: scenario.id,
    areaId: area.id,
    ...ZERO_TREES,
    name: area.type === "PARCEL" ? "Parcel" : "Right-of-way",
  }

  const initialValues: AreaOnScenarioValues = {
    ...areaInitialValues,
    // Existing target if the blockgroup is already in the scenario, otherwise the interactive
    // default (+5, capped at 100). The form always sends this back, so leaving the slider alone
    // re-writes the current value rather than clobbering it.
    treeEquityScoreTarget:
      blockgroupOnScenario?.treeEquityScoreTarget ??
      Math.min(100, blockgroup.tree_equity_score + DEFAULT_TARGET_INCREASE),
  }

  return (
    <Formik<AreaOnScenarioValues>
      enableReinitialize
      initialValues={initialValues}
      validate={validateZodSchema(schema)}
      onSubmit={async (values, { setErrors }) => {
        try {
          const { FORM_ERROR, ...otherErrors } = (await onSubmit(values)) || {}

          if (FORM_ERROR) {
            setFormError(FORM_ERROR)
          }

          if (Object.keys(otherErrors).length > 0) {
            setErrors(otherErrors)
          }
        } catch {
          setFormError("Failed to save. Please try again.")
        }
      }}
    >
      {({ setFieldValue, isSubmitting }) => {
        return (
          <Form>
            <div className="bg-gray-100">
              <div className="flex items-center justify-center pt-4">
                <span className="pr-2 text-center text-caption font-bold">
                  How many trees do you want to plant
                </span>
                <Help>
                  <span>
                    Document a planting plan for this site by adding trees. Available planting space
                    is defined by barren soil, low vegetation, and paved surfaces that are not
                    roads. Roads, structures, water, and existing canopy are excluded. Toggle
                    satellite imagery in the legend to better understand the layout of this site.
                    Site assessment is recommended and you can refine and adjust your saved site
                    plan at any point.
                  </span>
                </Help>
              </div>
              <div className="space-y-4 bg-gray-100 px-3 py-6">
                <div className="w-full pt-4 text-sm">
                  <div className="flex w-full justify-between">
                    <p className="pb-1 font-bold">Type</p>
                    <p className="pb-1 font-bold">Trees</p>
                    <p className="pb-1 pr-6 font-bold">
                      {city.locale && city.locale === "en-CA" ? "m²" : "ft²"}
                    </p>
                  </div>
                  <div className="">
                    {TYPES.map((type) => {
                      return (
                        <div key={type} className="flex w-full items-center justify-between">
                          <div className="w-1/5">{TREE_INFO[type].Type}</div>
                          <div className="w-2/5 p-1">
                            <Field name={type}>
                              {({ field }: FieldProps) => (
                                <input
                                  {...field}
                                  type="number"
                                  min="0"
                                  className="float-right w-28 rounded-sm border-gray-300 p-1 text-left text-caption tabular-nums"
                                  onBlur={(e) => {
                                    // Keep Formik's touched tracking, then normalize a cleared
                                    // input back to 0 so it never submits as "" (an empty number
                                    // <Field> stores ""), which the Zod schema would reject.
                                    field.onBlur(e)
                                    if (e.target.value === "") setFieldValue(type, 0)
                                  }}
                                />
                              )}
                            </Field>
                          </div>
                          <div className="w-1/5">
                            <CalculatedArea type={type} locale={city.locale} />
                          </div>

                          <div>
                            <button
                              type="button"
                              onClick={() => {
                                setFieldValue(type, 0)
                              }}
                              className="text-red-700 opacity-30 hover:opacity-100"
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-300 px-3 py-4">
                <p className="pb-3 text-body capitalize text-brand-green-dark">
                  Site planting overview
                </p>
                <div className="flex flex-row">
                  <div className="w-1/4">
                    <div className="text-subtitle font-semibold text-brand-green-dark">
                      {`${Math.floor(area.tree_canopy * 100)}%`}
                    </div>
                    <div className="text-sm capitalize text-gray-700">existing canopy</div>
                  </div>
                  <div className="w-1/4">
                    <PlantingIncrease area={potential_tree_canopy_sqft} p={area} />
                    <div className="text-caption capitalize text-gray-700">planned canopy</div>
                  </div>
                  <div className="flex w-2/4 flex-col">
                    <PlantingProgressBar area={potential_tree_canopy_sqft} />
                    <div className="flex flex-row justify-between">
                      <div>
                        <PlannedArea
                          className="text-caption text-brand-green-dark"
                          locale={city.locale}
                        />
                        <p className="text-center text-caption text-gray-700">proposed</p>
                      </div>
                      <div className="text-right">
                        <VersusArea
                          className="text-caption text-gray-700"
                          area={potential_tree_canopy_sqft}
                          locale={city.locale}
                        />
                        <div className="text-center text-caption text-gray-700">remaining</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <AreaWarning area={potential_tree_canopy_sqft} />
            </div>
            <BlockGroupOverview
              area={area}
              areaOnScenario={areaOnScenario}
              blockgroup={blockgroup}
              blockgroupScenario={blockgroupScenario}
              blockgroupOnScenario={blockgroupOnScenario}
              isSubmitting={isSubmitting}
              onCancel={onCancel}
            />
            {formError && (
              <div role="alert" className="p-3 text-sm text-red-500">
                {formError}
              </div>
            )}
          </Form>
        )
      }}
    </Formik>
  )
}

/**
 *
 * @returns sum of tree canopy added
 */
function usePlannedArea(): number {
  const { values } = useFormikContext<AreaOnScenario>()
  return getAreaSum(values)
}

function PlannedArea({ className, locale }: { className?: string; locale?: string }) {
  const sum = usePlannedArea()
  return (
    <span className={className}>
      {formatNumber(locale && locale === "en-CA" ? squareFeetToSquareMeters(sum) : sum)}{" "}
      {locale && locale === "en-CA" ? "m" : "ft"}
      <sup>2</sup>
    </span>
  )
}

function PlantingIncrease({ area, p }: { area: number; p: Area }) {
  const sum = usePlannedArea()
  const diff = area - sum
  const increase = Math.round((sum / squareKilometersToSquareFeet(p.area_sqkm)) * 100)
  return (
    <p
      className={`font-semibol text-subtitle ${
        diff < 0 ? "text-red-500" : "text-brand-green-dark"
      }`}
    >
      {`+${increase}%`}
    </p>
  )
}

function AreaWarning({ area }: { area: number }) {
  const sum = usePlannedArea()
  const diff = area - sum
  if (diff < 0) {
    return (
      <div className="flex items-center gap-x-2 px-4 py-2 text-annotation font-bold text-red-500">
        <ExclamationTriangleIcon className="flex-none" />
        You can still add these trees to your scenario, but more trees have been added than we
        estimate can fit in the selected space.
      </div>
    )
  }
  return null
}

function VersusArea({
  area,
  className,
  locale,
}: {
  area: number
  className?: string
  locale?: string
}) {
  const sum = usePlannedArea()
  const diff = area - sum
  return (
    <span className={className}>
      {formatNumber(locale && locale === "en-CA" ? squareFeetToSquareMeters(diff) : diff)}{" "}
      {locale && locale === "en-CA" ? "m" : "ft"}
      <sup>2</sup>
    </span>
  )
}

function CalculatedArea({ type, locale }: { type: TypeKey; locale?: string }) {
  const { values } = useFormikContext<AreaOnScenario>()
  if (typeof values[type] !== "number") return <div></div>
  const areaSqFt = values[type] * TREE_INFO[type].AreaSF
  return (
    <div className="whitespace-nowrap text-right tabular-nums">
      {formatNumber(locale && locale === "en-CA" ? squareFeetToSquareMeters(areaSqFt) : areaSqFt)}
    </div>
  )
}

function PlantingProgressBar({ area }: { area: number }) {
  const sum = usePlannedArea()
  const percentage = Math.round((sum / area) * 100)
  const diff = area - sum
  return (
    <div className="relative mb-2 mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-300">
      <div
        className={`absolute h-full ${diff < 0 ? "bg-red-500" : "bg-brand-green"} `}
        style={{ width: `${percentage}%` }}
      />
    </div>
  )
}
